import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useDrag, useDrop } from 'react-dnd';
import {
  LayoutDashboard, Users, CheckSquare, X, Inbox, Settings, Euro,
  Briefcase, BookOpen, ExternalLink, LogOut, User, Bell, Scale, Network,
  Send, ClipboardList, Gavel, FolderOpen, MessageCircle, Brain,
  Calendar, Mail, Landmark, History,
  Star, Eye, EyeOff, GripVertical, Pencil, RotateCcw
} from 'lucide-react';
import { useSidebarPrefs } from '@/hooks/useSidebarPrefs';
import { useTabs } from '@/contexts/TabContext';
import { useAuth } from '@/contexts/AuthContext';
import OlympusIcon from '@/components/OlympusIcon';
import ZeoIcon from '@/components/ZeoIcon';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export const ALL_NAV_ITEMS = [
  { path: '/', icon: LayoutDashboard, label: 'Cockpit' },
  { path: '/posteingang', icon: Inbox, label: 'Posteingang' },
  { path: '/eingangspruefung', icon: CheckSquare, label: 'Eingangsprüfung' },
  { path: '/akten', icon: Users, label: 'Betreuungsakten' },
  { path: '/netzwerk', icon: Network, label: 'Netzwerk' },
  { path: '/vorgaenge', icon: Landmark, label: 'Vorg\u00e4nge' },
  { path: '/aufgaben', icon: CheckSquare, label: 'Aufgaben' },
  { path: '/korrespondenz', icon: Send, label: 'Korrespondenz' },
  { path: '/antragsassistent', icon: ClipboardList, label: 'Antragsassistent' },
  { path: '/buchhaltung', icon: Euro, label: 'Buchhaltung' },
  { path: '/schulden-dashboard', icon: Scale, label: 'Schulden-Dashboard' },
  { path: '/fristen', icon: CheckSquare, label: 'Fristkalender' },
  { path: '/verguetung', icon: Euro, label: 'Verg\u00fctung (VBVG)' },
  { path: '/bueroorganisation', icon: Briefcase, label: 'B\u00fcroorganisation' },
  { path: '/buero-einstellungen', icon: Settings, label: 'B\u00fcro-Einstellungen' },
  { path: '/egvp', icon: Gavel, label: 'EGVP/beA' },
  { path: '/aktenuebernahme', icon: FolderOpen, label: 'Akten\u00fcbernahme' },
  { path: '/posterfassung', icon: Mail, label: 'KI-Posterfassung' },
  { path: '/whatsapp', icon: MessageCircle, label: 'WhatsApp' },
  { path: '/ki-ueberwachung', icon: Brain, label: 'KI-\u00dcberwachung' },
  { path: '/email-einstellungen', icon: Mail, label: 'E-Mail-Abruf' },
  { path: '/briefing', icon: Calendar, label: 'Wochen\u00fcbersicht' },
  { path: '/gericht', icon: Scale, label: 'Betreuungsgericht' },
  { path: '/assistent', icon: null, label: 'Zeo - KI-Assistent', customIcon: true },
  { path: '/zeo-verlauf', icon: History, label: 'Zeo Verlauf' },
  { path: '/wiki', icon: BookOpen, label: 'Betreuungs-Wiki' },
  { path: '/admin', icon: Settings, label: 'Administration', adminOnly: true },
  { path: '/benutzer', icon: User, label: 'Benutzerverwaltung', adminOnly: true },
];

const DND_TYPE = 'SIDEBAR_NAV_ITEM';

/* ──────────────── Draggable Nav Item ──────────────── */
function DraggableNavItem({ item, index, editMode, isFav, isHid, onToggleFav, onToggleHide, moveItem, onClose }) {
  const ref = useRef(null);
  const { openTab } = useTabs();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = !editMode && (
    location.pathname === item.path ||
    (item.path !== '/' && location.pathname.startsWith(item.path))
  );

  const [{ isDragging }, drag, preview] = useDrag({
    type: DND_TYPE,
    item: () => ({ index, path: item.path }),
    canDrag: editMode,
    collect: mon => ({ isDragging: mon.isDragging() }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: DND_TYPE,
    hover(dragged) {
      if (!ref.current || dragged.index === index) return;
      moveItem(dragged.index, index);
      dragged.index = index;
    },
    collect: mon => ({ isOver: mon.isOver() }),
  });

  preview(drop(ref));

  const handleClick = (e) => {
    e.preventDefault();
    if (editMode) return;
    openTab(item.path, { title: item.label });
    navigate(item.path);
    onClose();
  };

  return (
    <div
      ref={ref}
      data-testid={`sidebar-item-${item.path.replace(/\//g, '-').replace(/^-/, '') || 'home'}`}
      className={`
        group relative flex items-center rounded-lg transition-all duration-150
        ${isDragging ? 'opacity-30 scale-95' : ''}
        ${isOver && editMode ? 'bg-slate-700/40 ring-1 ring-blue-500/30' : ''}
        ${editMode && isHid ? 'opacity-40' : ''}
      `}
    >
      {editMode && (
        <div ref={drag} className="cursor-grab active:cursor-grabbing pl-1.5 pr-0.5 py-2.5 text-slate-600 hover:text-slate-300 flex-shrink-0">
          <GripVertical className="w-4 h-4" />
        </div>
      )}

      <a
        href={item.path}
        onClick={handleClick}
        className={`
          flex-1 flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors duration-150 min-w-0
          ${editMode ? 'cursor-default pointer-events-none' : ''}
          ${isActive ? 'bg-blue-500 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'}
        `}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {item.customIcon ? <ZeoIcon size={20} /> : item.icon && <item.icon className="w-5 h-5 flex-shrink-0" />}
        <span className="font-medium truncate">{item.label}</span>
        {!editMode && isFav && <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400 flex-shrink-0 ml-auto" />}
      </a>

      {editMode && (
        <div className="flex items-center gap-0.5 pr-1.5 flex-shrink-0">
          <button
            onClick={() => onToggleFav(item.path)}
            className={`p-1.5 rounded transition-colors ${isFav ? 'text-amber-400 hover:text-amber-300' : 'text-slate-600 hover:text-slate-300'}`}
            title={isFav ? 'Aus Favoriten entfernen' : 'Zu Favoriten'}
            data-testid={`sidebar-fav-${item.path.replace(/\//g, '-').replace(/^-/, '') || 'home'}`}
          >
            <Star className={`w-3.5 h-3.5 ${isFav ? 'fill-amber-400' : ''}`} />
          </button>
          <button
            onClick={() => onToggleHide(item.path)}
            className={`p-1.5 rounded transition-colors ${isHid ? 'text-red-400 hover:text-red-300' : 'text-slate-600 hover:text-slate-300'}`}
            title={isHid ? 'Wieder einblenden' : 'Ausblenden'}
            data-testid={`sidebar-hide-${item.path.replace(/\//g, '-').replace(/^-/, '') || 'home'}`}
          >
            {isHid ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
        </div>
      )}

      {!editMode && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openTab(item.path, { title: item.label });
            navigate(item.path);
            onClose();
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded
            opacity-0 group-hover:opacity-100 transition-opacity
            text-slate-500 hover:text-white hover:bg-slate-700"
          title="In neuem Tab oeffnen"
        >
          <ExternalLink className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

/* ──────────────── Sidebar ──────────────── */
export default function Sidebar({ isOpen, onClose, onOpenBenachrichtigungen, onOpenProfil }) {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState(false);
  const {
    prefs, toggleFavorite, toggleHidden, setCustomOrder, resetAll, isFavorite, isHidden,
  } = useSidebarPrefs();

  // Resizable sidebar
  const MIN_WIDTH = 200;
  const MAX_WIDTH = 480;
  const DEFAULT_WIDTH = 256;
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('olympus_sidebar_width');
    return saved ? Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, parseInt(saved, 10))) : DEFAULT_WIDTH;
  });
  const isResizing = useRef(false);

  const startResize = useCallback((e) => {
    e.preventDefault();
    isResizing.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (ev) => {
      if (!isResizing.current) return;
      const newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, ev.clientX));
      setSidebarWidth(newWidth);
    };

    const onMouseUp = () => {
      isResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      setSidebarWidth(w => { localStorage.setItem('olympus_sidebar_width', String(w)); return w; });
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  // Build sorted base items from prefs
  const baseItems = useMemo(() => {
    const items = ALL_NAV_ITEMS.filter(item => !item.adminOnly || isAdmin);
    const { order } = prefs;
    if (order.length > 0) {
      const orderMap = new Map(order.map((path, i) => [path, i]));
      return [...items].sort((a, b) => {
        const ai = orderMap.has(a.path) ? orderMap.get(a.path) : 999;
        const bi = orderMap.has(b.path) ? orderMap.get(b.path) : 999;
        if (ai === bi) return a.label.localeCompare(b.label, 'de');
        return ai - bi;
      });
    }
    return [...items].sort((a, b) => a.label.localeCompare(b.label, 'de'));
  }, [prefs.order, isAdmin]);

  // Local DnD state (only used in edit mode)
  const [dndItems, setDndItems] = useState(baseItems);
  useEffect(() => { if (!editMode) setDndItems(baseItems); }, [baseItems, editMode]);

  const moveItem = useCallback((dragIdx, hoverIdx) => {
    setDndItems(prev => {
      const next = [...prev];
      const [removed] = next.splice(dragIdx, 1);
      next.splice(hoverIdx, 0, removed);
      return next;
    });
  }, []);

  const handleToggleEdit = useCallback(() => {
    if (editMode) {
      setCustomOrder(dndItems.map(i => i.path));
    }
    setEditMode(prev => !prev);
  }, [editMode, dndItems, setCustomOrder]);

  const handleReset = useCallback(() => {
    resetAll();
    setEditMode(false);
  }, [resetAll]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Split for normal mode: favorites first, then rest, hidden filtered out
  const favItems = editMode ? [] : baseItems.filter(i => isFavorite(i.path) && !isHidden(i.path));
  const regularItems = editMode ? dndItems : baseItems.filter(i => !isFavorite(i.path) && !isHidden(i.path));

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />
      )}

      <aside
        data-testid="sidebar"
        style={{ width: sidebarWidth }}
        className={`
          fixed top-0 left-0 h-full bg-slate-900 z-50
          transform transition-transform duration-200 ease-in-out
          lg:translate-x-0 lg:static
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          flex flex-col flex-shrink-0
        `}
      >
        {/* Resize Handle */}
        <div
          onMouseDown={startResize}
          data-testid="sidebar-resize-handle"
          className="absolute top-0 right-0 w-1.5 h-full cursor-col-resize z-50 group hover:bg-blue-500/40 transition-colors"
          title="Ziehen zum Anpassen der Breite"
        >
          <div className="absolute top-1/2 -translate-y-1/2 right-0 w-1 h-12 rounded-full bg-slate-600 group-hover:bg-blue-400 transition-colors" />
        </div>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <OlympusIcon size={32} withBg />
            <span className="font-bold text-white">Olympus</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={onOpenBenachrichtigungen}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              title="Benachrichtigungs-Einstellungen"
              data-testid="btn-benachrichtigungen"
            >
              <Bell className="w-5 h-5" />
            </button>
            <button onClick={onClose} className="lg:hidden p-1 text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Edit Mode Bar */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-800/50">
          <button
            onClick={handleToggleEdit}
            className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors
              ${editMode
                ? 'bg-blue-500/20 text-blue-300 hover:bg-blue-500/30'
                : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800'
              }`}
            data-testid="sidebar-edit-toggle"
          >
            <Pencil className="w-3.5 h-3.5" />
            {editMode ? 'Fertig' : 'Anpassen'}
          </button>
          {editMode && (
            <button
              onClick={handleReset}
              className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs text-slate-500 hover:text-red-400 hover:bg-slate-800 transition-colors"
              title="Alle Anpassungen zuruecksetzen"
              data-testid="sidebar-reset-btn"
            >
              <RotateCcw className="w-3 h-3" />
              Reset
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto" data-testid="sidebar-nav">
          {/* Favorites (normal mode) */}
          {!editMode && favItems.length > 0 && (
            <>
              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-400/70">
                Favoriten
              </div>
              {favItems.map((item, i) => (
                <DraggableNavItem
                  key={item.path}
                  item={item}
                  index={i}
                  editMode={false}
                  isFav
                  isHid={false}
                  onToggleFav={toggleFavorite}
                  onToggleHide={toggleHidden}
                  moveItem={moveItem}
                  onClose={onClose}
                />
              ))}
              <div className="mx-3 my-2 border-t border-slate-700/50" />
            </>
          )}

          {/* Edit mode header */}
          {editMode && (
            <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              Ziehen zum Sortieren
            </div>
          )}

          {/* Regular / All Items */}
          {regularItems.map((item, i) => (
            <DraggableNavItem
              key={item.path}
              item={item}
              index={editMode ? i : i + favItems.length}
              editMode={editMode}
              isFav={isFavorite(item.path)}
              isHid={isHidden(item.path)}
              onToggleFav={toggleFavorite}
              onToggleHide={toggleHidden}
              moveItem={moveItem}
              onClose={onClose}
            />
          ))}
        </nav>

        {/* User */}
        <div className="p-4 border-t border-slate-800 flex-shrink-0">
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-slate-800 transition-colors">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                    {user.vorname?.[0]}{user.nachname?.[0]}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-white">{user.vorname} {user.nachname}</p>
                    <p className="text-xs text-slate-400">{user.rolle}</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuItem className="text-slate-500 text-xs">{user.email}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => openTab('/admin/profil')} data-testid="menu-profil-btn">
                  <User className="w-4 h-4 mr-2" />
                  Mein Profil
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Abmelden
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </aside>
    </>
  );
}
